import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import imgInfiniteVoxel from '../../assets/images/infinite-voxel-runner-bg.webp';
import vidEduSpaceFlowMac from '../../assets/images/edu-space-flow-mac.mp4';
import vidEduSpaceFlowMobile from '../../assets/images/edu-space-flow-mobile.mp4';
import imgAiVideo1 from '../../assets/images/ai-generate-video-news-1.webp';
import imgAiVideo2 from '../../assets/images/ai-generate-video-news-2.webp';
import imgTrain1 from '../../assets/images/training-management-1.webp';
import imgTrain2 from '../../assets/images/training-management-2.webp';
import imgTrain3 from '../../assets/images/training-management-3.webp';
import imgDesktop1 from '../../assets/images/desktop-app-csharp-1.webp';
import imgDesktop2 from '../../assets/images/desktop-app-csharp-2.webp';
import imgAutoMsg1 from '../../assets/images/auto-message-system-1.webp';
import imgAutoMsg2 from '../../assets/images/auto-message-system-2.webp';
import imgUniDocs1 from '../../assets/images/uni-docs-1.webp';
import imgUniDocs2 from '../../assets/images/uni-docs-2.webp';
import imgUniDocs3 from '../../assets/images/uni-docs-3.webp';
import imgUniDocs4 from '../../assets/images/uni-docs-4.webp';
import imgHotelCore1 from '../../assets/images/hotel-core-1.webp';
import imgEcommerce1 from '../../assets/images/e-commerce-1.webp';
import imgEcommerce2 from '../../assets/images/e-commerce-2.webp';
import imgPharma1 from '../../assets/images/pharma-sphere-1.webp';
import imgPharma2 from '../../assets/images/pharma-sphere-2.webp';
import imgPharma3 from '../../assets/images/pharma-sphere-3.webp';
import imgPharma4 from '../../assets/images/pharma-sphere-4.webp';
import imgPharma5 from '../../assets/images/pharma-sphere-5.webp';
import imgLuckyWheel1 from '../../assets/images/lucky-wheel-game-1.webp';
import imgLuckyWheel2 from '../../assets/images/lucky-wheel-game-2.webp';
import imgLuckyWheel3 from '../../assets/images/lucky-wheel-game-3.webp';
import imgLuckyWheel4 from '../../assets/images/lucky-wheel-game-4.webp';
import imgLuckyWheel5 from '../../assets/images/lucky-wheel-game-5.webp';
import imgTechResources1 from '../../assets/images/tech-resources-1.webp';
import {
  ExternalLink, Code2, 
  ChevronLeft, ChevronRight, X, ChevronDown, ChevronUp, Search,
  Info, ArrowRightLeft, Scale, Share, ArrowLeft, ArrowRight
} from 'lucide-react';
import Contact from './Contact';

interface Project {
  id: number;
  title: string;
  description: string;
  image: string;
  video?: string;
  videoMobile?: string;
  gallery?: string[];
  technologies: string[];
  platforms?: string[];
  github?: string;
  demo?: string;
  keyFeatures?: string[];
  challenges?: { problem: string, solution: string }[];
  category: string;
}

interface ProjectsProps {
  language: string;
}

const StatItem = ({ value, label, mobileLabel, suffix = "" }: { value: number, label: string, mobileLabel?: string, suffix?: string }) => {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    let frameId: number;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        if (entry.isIntersecting) {
          const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          if (prefersReducedMotion) {
            setCount(value);
            return;
          }

          let startTime: number | null = null;
          const duration = 1500; 

          const animate = (timestamp: number) => {
            if (!startTime) startTime = timestamp;
            const progress = timestamp - startTime;
            
            const percentage = Math.min(progress / duration, 1);
            const easeOut = 1 - Math.pow(1 - percentage, 4); 

            setCount(Math.floor(easeOut * value));

            if (progress < duration) {
              frameId = requestAnimationFrame(animate);
            } else {
              setCount(value); 
            }
          };

          frameId = requestAnimationFrame(animate);

        } else {
          if (frameId) cancelAnimationFrame(frameId);
          setCount(0);
        }
      },
      { threshold: 0.5 } 
    );

    observer.observe(element);
    return () => {
        if (frameId) cancelAnimationFrame(frameId);
        observer.disconnect();
    };
  }, [value]);

  return (
    <div ref={ref} className="flex flex-col items-center justify-center p-2">
      <div className="text-4xl md:text-5xl font-black text-blue-600 dark:text-blue-400 mb-2 tracking-tighter">
        {count.toLocaleString()}{suffix}
      </div>
      <div className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest text-center">
        <span className={mobileLabel ? "hidden md:inline" : ""}>{label}</span>
        {mobileLabel && <span className="md:hidden">{mobileLabel}</span>}
      </div>
    </div>
  );
};

const StatsSection = () => {
  return (
    <div className="w-full max-w-4xl mx-auto mt-8 mb-12">
      {/* Grid layout clean, không border, không background */}
      <div className="grid grid-cols-3 gap-4 md:gap-12">
        <StatItem value={15} label="Technologies" mobileLabel="Tech" suffix="+" />
        <StatItem value={20} label="Repositories" mobileLabel="Repo" suffix="+" />
        <StatItem value={1200} label="Commits" suffix="+" />
      </div>
    </div>
  );
};

interface DocModalProps {
    project: Project;
    projectNumber: number;
    onClose: () => void;
    language: string;
    onNextProject?: () => void;
    onPrevProject?: () => void;
}

const DocModal: React.FC<DocModalProps> = ({ project, onClose, language, projectNumber, onNextProject, onPrevProject }) => {
    const [activeImage, setActiveImage] = useState(project.gallery ? project.gallery[0] : project.image);
    const [copied, setCopied] = useState(false);

    const handleShare = async () => {
        const url = window.location.href;
        const title = `Tai Nguyen Portfolio - ${project.title}`;
        if (navigator.share) {
            try {
                await navigator.share({ title, url });
            } catch (err) {
                console.log('Error sharing', err);
            }
        } else {
            navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    useEffect(() => {
        setActiveImage(project.gallery ? project.gallery[0] : project.image);
    }, [project]);

    const [isExpanded, setIsExpanded] = useState(false);
    const [showFullScreen, setShowFullScreen] = useState(false);
    const [showManual, setShowManual] = useState(false);  
    const [showLegal, setShowLegal] = useState(false);

    const techLabel = language === 'vi' ? 'Công nghệ sử dụng:' : 'Technologies Used:';
    const descLabel = language === 'vi' ? 'Mô tả nội dung:' : 'Content Description:';

    const featuresLabel = language === 'vi' ? 'Tính năng chính:' : 'Key Features:';


    const closeLabel = language === 'vi' ? 'Đóng Dự án' : 'Close Project';
    const visitLabel = language === 'vi' ? 'Truy cập Website' : 'Visit Live Site';
    const readMoreLabel = language === 'vi' ? 'Xem thêm' : 'Read More';
    const showLessLabel = language === 'vi' ? 'Thu gọn' : 'Show Less';
    const galleryImages = project.gallery || [project.image];
    const currentIndex = galleryImages.indexOf(activeImage);
    
    const nextImage = (e: React.MouseEvent) => {
        e.stopPropagation();
        const nextIdx = (currentIndex + 1) % galleryImages.length;
        setActiveImage(galleryImages[nextIdx]);
    };
    
    const prevImage = (e: React.MouseEvent) => {
        e.stopPropagation();
        const prevIdx = (currentIndex - 1 + galleryImages.length) % galleryImages.length;
        setActiveImage(galleryImages[prevIdx]);
    };

    // Prevent background scroll
    useEffect(() => {
        const originalBodyStyle = document.body.style.overflow;
        const originalHtmlStyle = document.documentElement.style.overflow;
        
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
        
        return () => {
            document.body.style.overflow = originalBodyStyle;
            document.documentElement.style.overflow = originalHtmlStyle;
        };
    }, []);

    return createPortal(
        <>
            <div className="fixed inset-0 z-[9999] flex items-center justify-center p-0 md:p-10 bg-black/40 backdrop-blur-md animate-[fade-in_0.3s_ease-out]" onClick={onClose}>
                <div className="bg-white dark:bg-zinc-900 text-gray-900 dark:text-white w-full max-w-6xl h-[100dvh] md:h-[90vh] rounded-none md:rounded-[2.5rem] shadow-[0_40px_100px_rgba(0,0,0,0.7)] overflow-hidden flex flex-col md:flex-row border-0 md:border md:border-white/10 relative animate-[zoom-in_0.3s_ease-out]" onClick={e => e.stopPropagation()}>
                    
                    {/* LEFT SIDE: IMAGES (FIXED ON DESKTOP) */}
                    <div className="hidden md:flex md:w-[45%] lg:w-[40%] bg-gray-100 dark:bg-black/40 p-4 md:p-6 flex-col gap-4 overflow-hidden border-b md:border-b-0 md:border-r border-gray-200 dark:border-white/5 relative">
                        {/* Close button for mobile - REMOVED per user request */}

                        {/* Main Preview */}
                        <div className="relative group flex-1 rounded-3xl overflow-hidden bg-white dark:bg-zinc-950 shadow-inner min-h-[180px] sm:min-h-[250px] md:min-h-0 flex items-center justify-center border border-gray-200 dark:border-white/5">
                            {activeImage.endsWith('.mp4') ? (
                              <video 
                                  src={activeImage} 
                                  autoPlay 
                                  loop 
                                  muted 
                                  playsInline 
                                  className="w-full h-full object-contain transition-all duration-700 group-hover:scale-105"
                              />
                            ) : (
                              <img 
                                  src={activeImage} 
                                  alt={project.title} 
                                  className="w-full h-full object-contain transition-all duration-700 group-hover:scale-105"
                                  draggable="false"
                                  decoding="async"
                                  fetchPriority="high"
                              />
                            )}

                            {/* Updating Note */}
                            {activeImage.includes('data:image/svg') && (
                                <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-transparent text-gray-800 text-[10px] font-bold flex items-center z-20">
                                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 mr-1.5 animate-pulse shadow-[0_0_8px_rgba(34,197,94,0.6)]"></span>
                                    {language === 'vi' ? 'Đang cập nhật' : 'Updating'}
                                    <span className="flex w-3 text-left ml-0.5">
                                      <span className="dot-1">.</span>
                                      <span className="dot-2">.</span>
                                      <span className="dot-3">.</span>
                                    </span>
                                </div>
                            )}
                            
                            {/* Navigation Arrows */}
                            {galleryImages.length > 1 && (
                                <>
                                    <button 
                                        onClick={prevImage}
                                        className="absolute left-4 p-2 bg-white/10 backdrop-blur-md rounded-full text-white border border-white/20 opacity-0 group-hover:opacity-100 transition-all hover:bg-blue-500 shadow-xl z-30"
                                    >
                                        <ChevronLeft size={20} />
                                    </button>
                                    <button 
                                        onClick={nextImage}
                                        className="absolute right-4 p-2 bg-white/10 backdrop-blur-md rounded-full text-white border border-white/20 opacity-0 group-hover:opacity-100 transition-all hover:bg-blue-500 shadow-xl z-30"
                                    >
                                        <ChevronRight size={20} />
                                    </button>
                                </>
                            )}

                            {/* Pagination Dots */}
                            {galleryImages.length > 1 && (
                                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2.5 z-30 bg-black/20 backdrop-blur-sm px-3 py-2 rounded-full border border-white/10">
                                    {galleryImages.map((_, idx) => (
                                        <button
                                            key={idx}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setActiveImage(galleryImages[idx]);
                                            }}
                                            className={`transition-all duration-500 rounded-full ${
                                                currentIndex === idx 
                                                ? 'bg-blue-500 w-4 h-1.5' 
                                                : 'bg-transparent border border-white/40 w-1.5 h-1.5 hover:border-white/70 hover:bg-white/10'
                                            }`}
                                            aria-label={`Go to image ${idx + 1}`}
                                        />
                                    ))}
                                </div>
                            )}

                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                                <button 
                                    onClick={() => setShowFullScreen(true)}
                                    className="p-4 bg-white/10 backdrop-blur-md rounded-full text-white border border-white/20 opacity-0 group-hover:opacity-100 transform scale-90 group-hover:scale-100 transition-all duration-300 hover:bg-blue-500 hover:border-blue-400 shadow-xl"
                                >
                                    <Search size={24} />
                                </button>
                            </div>
                        </div>

                        {/* Thumbnail Grid */}
                        <div className="hidden md:grid grid-cols-4 sm:grid-cols-5 md:grid-cols-4 lg:grid-cols-5 gap-3 py-2">
                            {galleryImages.map((img, idx) => (
                                <button 
                                    key={idx}
                                    onClick={() => setActiveImage(img)}
                                    className={`relative aspect-square rounded-2xl overflow-hidden border-2 transition-all duration-300 ${activeImage === img ? 'border-blue-500 scale-95 ring-4 ring-blue-500/20' : 'border-transparent hover:border-white/20'}`}
                                >
                                    {img.endsWith('.mp4') ? (
                                        <video 
                                            src={img} 
                                            className="w-full h-full object-cover" 
                                            muted 
                                            playsInline 
                                        />
                                    ) : (
                                        <img 
                                            src={img} 
                                            alt={`${project.title} thumb ${idx}`} 
                                            className="w-full h-full object-cover" 
                                            draggable="false" 
                                            loading="lazy"
                                            decoding="async"
                                            fetchPriority="low"
                                        />
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* RIGHT SIDE: CONTENT (SCROLLABLE) */}
                    <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                        {/* Header - Fixed Height */}
                        <div className="shrink-0 p-6 md:p-8 flex justify-between items-start border-b-2 border-gray-200 dark:border-white/10 bg-gray-50/50 dark:bg-white/[0.02] relative">
                            <div>
                                <div className="text-[10px] font-bold text-blue-500 uppercase tracking-[0.2em] mb-1 opacity-80">
                                    {language === 'vi' ? 'DỰ ÁN' : 'PROJECT'} #{projectNumber.toString().padStart(2, '0')}
                                </div>
                                <h2 className="text-2xl md:text-3xl font-black tracking-tight">{project.title}</h2>
                            </div>
                            {/* Actions Group - Desktop only */}
                            <div className="hidden md:flex relative gap-2 mt-1">
                                {/* Legal Icon */}
                                <div className="relative group">
                                    <button 
                                        onClick={() => { setShowLegal(!showLegal); setShowManual(false); }}
                                        className={`p-2 rounded-full border transition-all duration-300 shadow-sm ${showLegal ? 'bg-blue-500 text-white border-blue-500' : 'bg-white dark:bg-zinc-800 text-gray-500 hover:text-blue-500 hover:border-blue-500 border-gray-200 dark:border-zinc-700'}`}
                                    >
                                        <Scale size={20} />
                                    </button>
                                    <div className="absolute right-0 top-full mt-2 w-max px-3 py-1.5 bg-gray-900 dark:bg-black text-white text-[11px] font-bold rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 shadow-xl">
                                        {language === 'vi' ? 'Thông tin Pháp lý' : 'Legal Disclaimer'}
                                        <div className="absolute right-3 -top-1 w-2 h-2 bg-gray-900 dark:bg-black transform rotate-45"></div>
                                    </div>
                                </div>

                                {/* Manual Info Icon */}
                                <div className="relative group">
                                    <button 
                                        onClick={() => { setShowManual(!showManual); setShowLegal(false); }}
                                        className={`p-2 rounded-full border transition-all duration-300 shadow-sm ${showManual ? 'bg-blue-500 text-white border-blue-500' : 'bg-white dark:bg-zinc-800 text-gray-500 hover:text-blue-500 hover:border-blue-500 border-gray-200 dark:border-zinc-700'}`}
                                    >
                                        <Info size={20} />
                                    </button>
                                    {/* Tooltip */}
                                    <div className="absolute right-0 top-full mt-2 w-max px-3 py-1.5 bg-gray-900 dark:bg-black text-white text-[11px] font-bold rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 shadow-xl">
                                        {language === 'vi' ? 'Tài liệu hướng dẫn sử dụng' : 'User Manual Documentation'}
                                        <div className="absolute right-3 -top-1 w-2 h-2 bg-gray-900 dark:bg-black transform rotate-45"></div>
                                    </div>
                                </div>

                                {/* Share Icon */}
                                <div className="relative group">
                                    <button 
                                        onClick={handleShare}
                                        className="p-2 rounded-full border transition-all duration-300 shadow-sm bg-white dark:bg-zinc-800 text-gray-500 hover:text-blue-500 hover:border-blue-500 border-gray-200 dark:border-zinc-700"
                                    >
                                        <Share size={20} />
                                    </button>
                                    <div className="absolute right-0 top-full mt-2 w-max px-3 py-1.5 bg-gray-900 dark:bg-black text-white text-[11px] font-bold rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 shadow-xl">
                                        {copied ? (language === 'vi' ? 'Đã sao chép!' : 'Copied!') : (language === 'vi' ? 'Chia sẻ dự án' : 'Share Project')}
                                        <div className="absolute right-3 -top-1 w-2 h-2 bg-gray-900 dark:bg-black transform rotate-45"></div>
                                    </div>
                                </div>

                                {/* Prev Project Icon */}
                                {onPrevProject && (
                                    <div className="relative group">
                                        <button 
                                            onClick={onPrevProject}
                                            className="p-2 rounded-full border transition-all duration-300 shadow-sm bg-white dark:bg-zinc-800 text-gray-500 hover:text-blue-500 hover:border-blue-500 border-gray-200 dark:border-zinc-700"
                                        >
                                            <ArrowLeft size={20} />
                                        </button>
                                        <div className="absolute right-0 top-full mt-2 w-max px-3 py-1.5 bg-gray-900 dark:bg-black text-white text-[11px] font-bold rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 shadow-xl">
                                            {language === 'vi' ? 'Dự án trước' : 'Previous Project'}
                                            <div className="absolute right-3 -top-1 w-2 h-2 bg-gray-900 dark:bg-black transform rotate-45"></div>
                                        </div>
                                    </div>
                                )}

                                {/* Next Project Icon */}
                                {onNextProject && (
                                    <div className="relative group">
                                        <button 
                                            onClick={onNextProject}
                                            className="p-2 rounded-full border transition-all duration-300 shadow-sm bg-white dark:bg-zinc-800 text-gray-500 hover:text-blue-500 hover:border-blue-500 border-gray-200 dark:border-zinc-700"
                                        >
                                            <ArrowRight size={20} />
                                        </button>
                                        <div className="absolute right-0 top-full mt-2 w-max px-3 py-1.5 bg-gray-900 dark:bg-black text-white text-[11px] font-bold rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 shadow-xl">
                                            {language === 'vi' ? 'Dự án tiếp theo' : 'Next Project'}
                                            <div className="absolute right-3 -top-1 w-2 h-2 bg-gray-900 dark:bg-black transform rotate-45"></div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Scrollable Info Area - Takes remaining space */}
                        <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-10 custom-scrollbar overflow-x-hidden">
                            {showLegal ? (
                                /* LEGAL CONTENT */
                                <div className="animate-[fade-in_0.3s_ease-out] space-y-6">
                                    <h3 className="text-xl md:text-2xl font-black text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-white/10 pb-4 flex items-center gap-3">
                                        <Scale size={28} />
                                        {language === 'vi' ? 'Thông tin Pháp lý & Tuyên bố Từ chối Trách nhiệm' : 'Legal Disclaimer'}
                                    </h3>
                                    <div className="bg-gray-50 dark:bg-gray-800/40 border border-slate-200/60 dark:border-zinc-800 p-6 rounded-2xl shadow-sm">
                                        <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-justify text-sm md:text-base">
                                            {language === 'vi' 
                                                ? 'Toàn bộ các dự án trong danh mục này được phát triển hoàn toàn vì mục đích học tập, nghiên cứu và xây dựng danh mục đầu tư cá nhân (portfolio). Nội dung, số liệu và các sản phẩm không có giá trị pháp lý, không nhằm mục đích thương mại và không cố ý vi phạm bản quyền của bất kỳ bên thứ ba nào. Mọi sự tương đồng với các sản phẩm, tổ chức hay cá nhân thực tế (nếu có) đều là ngẫu nhiên và phục vụ cho mục đích giáo dục.' 
                                                : 'All projects in this portfolio are developed strictly for educational, research, and personal portfolio building purposes. The content, data, and products hold no legal value, are not intended for commercial use, and do not intentionally infringe on any third-party copyrights. Any resemblance to real-world products, organizations, or individuals is purely coincidental and serves educational purposes.'}
                                        </p>
                                    </div>
                                </div>
                            ) : showManual ? (
                                /* MANUAL CONTENT */
                                <div className="animate-[fade-in_0.3s_ease-out] space-y-8">
                                    <h3 className="text-xl md:text-2xl font-black text-gray-900 dark:text-white border-b border-gray-200 dark:border-white/10 pb-4 flex items-center gap-3">
                                        <Info size={28} className="text-blue-500" />
                                        {language === 'vi' ? 'Tài liệu Hướng dẫn Sử dụng' : 'User Manual Documentation'}
                                    </h3>
                                    
                                    <div className="space-y-6 text-gray-700 dark:text-gray-300 text-sm md:text-base leading-relaxed">
                                        <p className="font-medium italic text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-white/5 p-4 rounded-xl border border-gray-100 dark:border-white/10">
                                            {language === 'vi' 
                                              ? `Dự án ${project.title} được thiết kế với kiến trúc tối ưu, tập trung vào khả năng mở rộng, bảo mật và cung cấp trải nghiệm người dùng chất lượng cao.`
                                              : `The ${project.title} project is designed with an optimal architecture, focusing on scalability, security, and delivering a high-quality user experience.`}
                                        </p>
                                        
                                        <div className="space-y-3">
                                            <h4 className="font-bold text-gray-900 dark:text-white text-lg flex items-center gap-2">
                                                <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                                                {language === 'vi' ? '1. Yêu cầu Hệ thống & Môi trường' : '1. System & Environment Requirements'}
                                            </h4>
                                            <ul className="list-disc pl-6 space-y-2 opacity-90">
                                                <li>
                                                    <strong>{language === 'vi' ? 'Ngôn ngữ/Công nghệ cốt lõi: ' : 'Core Technologies: '}</strong>
                                                    {project.technologies.slice(0, 4).join(', ')}
                                                </li>
                                                {project.platforms && (
                                                    <li>
                                                        <strong>{language === 'vi' ? 'Môi trường triển khai: ' : 'Deployment Environment: '}</strong>
                                                        {project.platforms.join(', ')}
                                                    </li>
                                                )}
                                                <li>
                                                    <strong>{language === 'vi' ? 'Yêu cầu phụ thuộc: ' : 'Dependencies Required: '}</strong>
                                                    {language === 'vi' ? 'Cài đặt các gói thư viện tương thích với phiên bản mới nhất của framework.' : 'Install package dependencies compatible with the latest framework version.'}
                                                </li>
                                            </ul>
                                        </div>

                                        <div className="space-y-3">
                                            <h4 className="font-bold text-gray-900 dark:text-white text-lg flex items-center gap-2">
                                                <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                                                {language === 'vi' ? '2. Hướng dẫn Cài đặt & Khởi chạy' : '2. Setup & Execution Guide'}
                                            </h4>
                                            <div className="space-y-3 opacity-90">
                                                <p><strong>Bước 1:</strong> {language === 'vi' ? 'Clone mã nguồn từ kho lưu trữ Github chính thức của dự án.' : 'Clone the source code from the official project Github repository.'}</p>
                                                <div className="bg-gray-100 dark:bg-black/50 p-3 rounded-lg text-xs text-blue-600 dark:text-blue-400 border border-gray-200 dark:border-white/10 overflow-x-auto">
                                                    git clone {project.github}
                                                </div>
                                                <p><strong>Bước 2:</strong> {language === 'vi' ? 'Thiết lập các biến môi trường (Environment Variables), kết nối cơ sở dữ liệu và cấu hình cần thiết theo tài liệu README đính kèm.' : 'Configure Environment Variables, database connections, and required settings according to the attached README documentation.'}</p>
                                                <p><strong>Bước 3:</strong> {language === 'vi' ? 'Khởi chạy server thông qua CLI và truy cập vào localhost theo cổng được chỉ định để kiểm thử.' : 'Start the server via CLI and access localhost on the designated port for testing.'}</p>
                                            </div>
                                        </div>

                                        <div className="space-y-3">
                                            <h4 className="font-bold text-gray-900 dark:text-white text-lg flex items-center gap-2">
                                                <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                                                {language === 'vi' ? '3. Cấu trúc Tính năng Cốt lõi' : '3. Core Feature Structure'}
                                            </h4>
                                            <p className="opacity-90">
                                                {language === 'vi' 
                                                  ? 'Hệ thống cung cấp các chức năng chính yếu sau đã được đóng gói an toàn và dễ bảo trì:' 
                                                  : 'The system provides the following essential functionalities, securely packaged and maintainable:'}
                                            </p>
                                            <ul className="list-disc pl-6 space-y-2 opacity-90">
                                                {project.keyFeatures?.map((feat, idx) => <li key={idx}>{feat}</li>) || <li>{language === 'vi' ? 'Đang cập nhật tính năng' : 'Features updating...'}</li>}
                                            </ul>
                                        </div>

                                        <div className="space-y-3">
                                            <h4 className="font-bold text-gray-900 dark:text-white text-lg flex items-center gap-2">
                                                <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                                                {language === 'vi' ? '4. Lưu ý Bảo mật & Vận hành' : '4. Security & Operations Notes'}
                                            </h4>
                                            <p className="opacity-90">
                                                {language === 'vi' 
                                                  ? 'Vui lòng đảm bảo không để lộ các tệp tin cấu hình nhạy cảm. Hệ thống đã được tích hợp các cơ chế xác thực/phân quyền an toàn để vận hành ổn định trong môi trường thực tế. Quá trình triển khai lên server cần thông qua các công cụ CI/CD để đảm bảo luồng phân phối mượt mà.' 
                                                  : 'Please ensure sensitive configuration files are not exposed. The system has integrated secure authentication/authorization mechanisms for stable operation in production environments. Deployment to the server should go through CI/CD pipelines to ensure a smooth delivery flow.'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {/* 1. Description Section - TOP */}
                            <div className="space-y-4">
                                <h3 className="text-[11px] font-bold uppercase tracking-widest text-gray-400">
                                    {descLabel}
                                </h3>
                                <p className="text-gray-600 dark:text-gray-400 leading-relaxed text-sm md:text-[15px] font-medium italic opacity-90">
                                    "{project.description}"
                                </p>
                            </div>

                            {/* 2. Technologies Section */}
                            <div className="space-y-4">
                                <h3 className="text-[11px] font-bold uppercase tracking-widest text-gray-400">
                                    {techLabel}
                                </h3>
                                <div className="flex flex-wrap gap-2">
                                    {project.technologies.map((tech, i) => (
                                        <span key={i} className="px-3 py-1.5 bg-gray-100 dark:bg-blue-500/10 text-gray-700 dark:text-blue-300 rounded-lg text-[11px] font-bold border border-gray-200 dark:border-blue-500/20">
                                            {tech}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            {/* 3. Key Features Section */}
                            {project.keyFeatures && (
                                <div className="space-y-4">
                                    <h3 className="text-[11px] font-bold uppercase tracking-widest text-gray-400">
                                        {featuresLabel}
                                    </h3>
                                    <div className="relative group/section">
                                        <div className={`relative transition-all duration-500 ease-in-out overflow-hidden ${!isExpanded ? 'max-h-24' : 'max-h-[5000px]'}`}>
                                            <ul className="space-y-3">
                                                {(isExpanded ? project.keyFeatures : project.keyFeatures.slice(0, 2)).map((feature, i) => (
                                                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                                                        <span className="text-blue-500 font-bold">-</span>
                                                        {feature}
                                                    </li>
                                                ))}
                                            </ul>
                                            {!isExpanded && project.keyFeatures.length > 2 && (
                                                <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white dark:from-zinc-900 via-white/80 dark:via-zinc-900/80 to-transparent flex items-end justify-center">
                                                    <button 
                                                        onClick={() => setIsExpanded(true)}
                                                        className="text-blue-500 text-[10px] font-black uppercase tracking-wider hover:text-blue-600 transition-all flex items-center gap-1.5 border-b border-blue-500/30 hover:border-blue-500 pb-0.5 mb-2"
                                                    >
                                                        {readMoreLabel} <ChevronDown size={12} />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* --- COLLAPSIBLE CONTENT BELOW --- */}
                            {isExpanded && (
                                <div className="space-y-10 animate-[fade-in_0.5s_ease-out]">

                                    {/* Global Show Less Button */}
                                    <div className="pt-4 flex justify-center">
                                        <button 
                                            onClick={() => setIsExpanded(false)}
                                            className="text-gray-400 hover:text-blue-500 text-[10px] font-black uppercase tracking-wider transition-all flex items-center gap-1.5 border-b border-transparent hover:border-blue-500/30 pb-0.5"
                                        >
                                            {showLessLabel} <ChevronUp size={12} />
                                        </button>
                                    </div>
                                </div>
                            )}
                                </>
                            )}
                        </div>

                        {/* Footer Actions - Fixed Height */}
                        <div className="shrink-0 p-6 md:p-8 bg-gray-50/80 dark:bg-black/20 border-t border-gray-100 dark:border-white/5 flex flex-col sm:flex-row gap-3">
                            {project.demo && (
                                <button 
                                    onClick={() => window.open(project.demo, '_blank')}
                                    className="flex-1 px-6 py-3.5 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl font-bold text-sm transition-all shadow-lg shadow-blue-500/20 flex items-center justify-center gap-2 group"
                                >
                                    <ExternalLink size={18} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                                    {visitLabel}
                                </button>
                            )}
                            <button 
                                onClick={onClose}
                                className="flex-1 px-6 py-3.5 bg-white dark:bg-white/5 hover:bg-red-50 dark:hover:bg-red-500/10 text-gray-700 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 rounded-2xl font-bold text-sm border border-gray-200 dark:border-white/10 hover:border-red-200 dark:hover:border-red-500/20 transition-all flex items-center justify-center gap-2 shadow-sm"
                            >
                                <X size={18} />
                                {closeLabel}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* FULL SCREEN LIGHTBOX */}
            {showFullScreen && (
                <div className="fixed inset-0 z-[10000] bg-black/95 flex items-center justify-center p-4 animate-[fade-in_0.2s_ease-out]" onClick={() => setShowFullScreen(false)}>
                    <button className="absolute top-6 right-6 z-[10001] p-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-all border border-white/20 backdrop-blur-md shadow-2xl">
                        <X size={24} />
                    </button>
                    <div className="w-full h-full flex items-center justify-center p-8 md:p-20">
                        {activeImage.endsWith('.mp4') ? (
                          <video 
                              src={activeImage} 
                              autoPlay 
                              loop 
                              muted 
                              playsInline 
                              className="max-w-full max-h-full object-contain rounded-xl shadow-2xl"
                          />
                        ) : (
                          <img 
                              src={activeImage} 
                              alt="Full screen preview" 
                              className="max-w-full max-h-full object-contain rounded-xl shadow-2xl"
                          />
                        )}
                    </div>
                </div>
            )}
        </>,
        document.body
    );
};

const Projects: React.FC<ProjectsProps> = ({ language }) => {
  const [selectedDocProject, setSelectedDocProject] = useState<Project | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showContactModal, setShowContactModal] = useState(false);
  const projectsPerPage = 6;

  const containerRef = useRef<HTMLDivElement>(null);

  const scrollProgressRef = useRef(0);
  const isLocked = useRef(false);

  useEffect(() => {
    scrollProgressRef.current = scrollProgress;
  }, [scrollProgress]);

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (!containerRef.current || window.innerWidth < 768) return;
      
      const rect = containerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const currentProgress = scrollProgressRef.current;
      
      // 1. Detect if the section is entering the sticky viewport range from top (scrolling down)
      // If the top of the container is near or has passed the top of screen (<= 150px)
      // and we haven't completed the animation yet (currentProgress < 1)
      if (!isLocked.current && currentProgress < 1 && rect.top <= 150 && rect.bottom >= 300 && e.deltaY > 0) {
        isLocked.current = true;
        
        // Smoothly snap/scroll the window so the sticky container is perfectly aligned
        const targetScrollY = window.scrollY + rect.top;
        window.scrollTo({
          top: targetScrollY,
          behavior: 'smooth'
        });
      }
      
      // 2. Detect if the section is entering the sticky viewport range from bottom (scrolling up)
      // If the bottom of the container is entering from below (rect.bottom >= viewportHeight - 150)
      // and we have started the animation (currentProgress > 0)
      if (!isLocked.current && currentProgress > 0 && rect.bottom >= viewportHeight - 150 && rect.top <= viewportHeight - 300 && e.deltaY < 0) {
        isLocked.current = true;
        
        const targetScrollY = window.scrollY + rect.top;
        window.scrollTo({
          top: targetScrollY,
          behavior: 'smooth'
        });
      }
      
      // 3. If the section is locked, hijack the wheel event completely
      if (isLocked.current) {
        let shouldRelease = false;
        
        setScrollProgress((prev) => {
          // Speed step: 0.005 for ultra-slow, premium, gradual reveal pacing
          const step = 0.005;
          const next = e.deltaY > 0 ? prev + step : prev - step;
          const clamped = Math.min(Math.max(next, 0), 1);
          
          if ((e.deltaY > 0 && clamped >= 1) || (e.deltaY < 0 && clamped <= 0)) {
            shouldRelease = true;
          }
          
          return clamped;
        });
        
        if (e.cancelable) {
          e.preventDefault();
        }
        
        if (shouldRelease) {
          isLocked.current = false;
        }
      }
    };

    // Monitor standard scroll to reset lock state if the section is scrolled away by external actions
    const handleScrollReset = () => {
      if (!containerRef.current || window.innerWidth < 768) return;
      const rect = containerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      
      if (rect.bottom < -100 || rect.top > viewportHeight + 100) {
        isLocked.current = false;
      }
    };

    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('scroll', handleScrollReset, { passive: true });
    
    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('scroll', handleScrollReset);
    };
  }, []);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    const element = document.getElementById('all-projects');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const translations = {
    en: {
      featuredTitle: 'Featured Projects',
      sideTitle: 'Experimental Lab',
      viewCode: 'View Code',
      liveDemo: 'Live Demo',
      readDocs: 'View Detail', 
      technologies: 'Technologies',
      platforms: 'Platforms',
    },
    vi: {
      featuredTitle: 'DỰ ÁN NỔI BẬT',
      sideTitle: 'PHÒNG THÍ NGHIỆM',
      viewCode: 'Xem Code',
      liveDemo: 'Xem Demo',
      readDocs: 'Xem Chi Tiết',
      technologies: 'Công Nghệ',
      platforms: 'Nền Tảng',
    }
  };

  const t = translations[language as keyof typeof translations];
  const isVietnamese = language === 'vi';

  // --- DATA ---
    const featuredProjects: Project[] = [
      {
        id: 1000,
        title: 'EduSpaceFlow',
        description: isVietnamese ? 'Một nền tảng Công nghệ Giáo dục (EdTech) toàn diện với cổng thông tin kép, được thiết kế dành cho gia sư độc lập nhằm quản lý lớp học, lịch học, học phí và giao tiếp với học sinh theo thời gian thực một cách mượt mà.' : 'A comprehensive, dual-portal Educational Technology (EdTech) platform designed for independent tutors to seamlessly manage classes, schedules, tuition fees, and student communications in real-time.',
        image: imgTechResources1,
        video: vidEduSpaceFlowMac,
        videoMobile: vidEduSpaceFlowMobile,
        gallery: [vidEduSpaceFlowMac, vidEduSpaceFlowMobile],
        technologies: ['C# .NET 8', 'EF Core', 'PostgreSQL', 'Supabase', 'React', 'Tailwind'],
        demo: 'https://eduspaceflow.pages.dev/',
        category: 'Website',
        keyFeatures: isVietnamese ? [
          'Kiến trúc cổng thông tin kép: Giao diện riêng biệt, đồng bộ hoàn hảo dành cho Gia sư và Học sinh.',
          'Thông báo thời gian thực: Hệ thống thông báo theo mức độ ưu tiên kèm huy hiệu chưa đọc, theo dõi trạng thái đã xem và đính kèm tệp.',
          'Quản lý tập trung: Kiểm soát toàn diện tài khoản học sinh, lớp học, bài tập và học phí.',
          'Tương tác hiện đại: Bao gồm các tính năng nâng cao như Điểm danh bằng mã QR và luồng phê duyệt Yêu cầu xin nghỉ tự động.'
        ] : [
          'Dual Portals Architecture: Dedicated, perfectly synchronized interfaces for both Tutors and Students.',
          'Real-time Announcements: Priority-based notification system with unread badges, receipt tracking, and file attachments.',
          'Centralized Management: Complete control over student accounts, classes, assignments, and tuition fees.',
          'Modern Interactions: Includes advanced features like QR Code Attendance and an automated Leave Request approval workflow.'
        ],
        platforms: ['Monorepo', 'Cloudflare Pages', 'Render']
      },
            {
      id: 4,
      title: 'UniDocs',
      description: isVietnamese ? 'Nền tảng chia sẻ và quản lý tài liệu học thuật mã nguồn mở được xây dựng bằng Java Spring Boot và Supabase S3.' : 'An open-source academic document management and sharing platform built with Java Spring Boot and Supabase S3.',
      image: imgUniDocs1,
      gallery: [imgUniDocs1, imgUniDocs2, imgUniDocs3, imgUniDocs4],
      technologies: ['Java', 'Spring Boot', 'PostgreSQL', 'Supabase S3'],
      platforms: ['Web'],
      github: 'https://github.com/ntainguyenit/Unidocs-Platform',
      demo: 'https://unidocs-husc.pages.dev/',
      category: 'Website',
      keyFeatures: isVietnamese ? ['Quản lý tài liệu học thuật mã nguồn mở.', 'Tích hợp lưu trữ Supabase S3.', 'Giao diện thân thiện và dễ sử dụng.'] : ['Open-source academic document management.', 'Supabase S3 storage integration.', 'User-friendly and intuitive interface.'],
    },
    {
      id: 107,
      title: 'E-commerce',
      description: isVietnamese 
        ? 'Hệ thống quản lý bán hàng toàn diện với đầy đủ phân hệ Admin và Shop, hỗ trợ quản lý kho, đơn hàng và khách hàng.'
        : 'A comprehensive sales management system with Admin and Shop modules, supporting inventory, orders, and customer management.',
      image: imgEcommerce1,
      gallery: [imgEcommerce1, imgEcommerce2],
      technologies: ['C#', 'ASP.NET Core MVC', 'SQL Server', 'Business Layers', 'Data Layers'],
      github: 'https://github.com/ntainguyenit/SV23T1020463',
      demo: 'https://youtu.be/c52uZfGXxJs',
      category: 'Website',
      keyFeatures: isVietnamese ? ['Phân hệ Admin và Shop riêng biệt.', 'Quản lý kho hàng thông minh.', 'Xử lý giỏ hàng và thanh toán.'] : ['Separate Admin and Shop modules.', 'Smart inventory management.', 'Cart and checkout processing.'],
    },
    {
      id: 999,
      title: 'AI Generate Video News',
      description: isVietnamese 
        ? 'Một End-to-End Automation Pipeline viết bằng Python, ứng dụng LLM (Gemini) và Edge-TTS để tự động chuyển đổi bài báo thành video định dạng dọc. Hệ thống tích hợp khả năng xử lý đồ họa động, đồng bộ hóa âm thanh - phụ đề theo thời gian thực (Zero-latency) và quản lý tiến trình tự động qua Telegram Bot.' 
        : 'An End-to-End Automation Pipeline written in Python, applying LLM (Gemini) and Edge-TTS to automatically convert news articles into vertical videos. The system integrates dynamic graphics processing, real-time audio-subtitle synchronization (Zero-latency), and automated process management via Telegram Bot.',
      image: imgAiVideo1,
      gallery: [imgAiVideo1, imgAiVideo2],
      technologies: ['Python', 'Google Gemini API', 'Edge-TTS', 'MoviePy', 'Pillow (PIL)', 'FFmpeg', 'Newspaper3k', 'Telegram Bot API'],
      platforms: ['CLI'],
      github: 'https://github.com/ntainguyenit/AI-Generate-Video-News',
      category: 'CLI',
      keyFeatures: isVietnamese
        ? [
            'Tự động hóa hoàn toàn (Zero-touch): Trích xuất nội dung từ URL, tóm tắt bằng AI và lên kịch bản video mà không cần can thiệp thủ công.',
            'Xử lý Media chuyên sâu: Ép khung hình chuẩn Reels/TikTok, render hiệu ứng phụ đề Karaoke đồng bộ chính xác từng mili-giây với sóng âm.',
            'Khả năng chịu lỗi cao (Fault Tolerance): Tích hợp Session Caching để khôi phục tiến trình khi crash và tự động gửi video thành phẩm qua Telegram.'
          ]
        : [
            'Full automation (Zero-touch): Extracts content from URL, summarizes via AI, and scripts video without manual intervention.',
            'Advanced Media Processing: Standardizes frames for Reels/TikTok, renders Karaoke subtitle effects synced precisely to the millisecond with audio waves.',
            'High Fault Tolerance: Integrates Session Caching for crash recovery and automated delivery of finished videos via Telegram.'
          ],
    },
    {
      id: 114,
      title: 'Pharma Sphere',
      description: isVietnamese 
        ? 'Hệ thống Quản lý Nhà thuốc chuyên nghiệp được xây dựng bằng ASP.NET MVC, Entity Framework và SQL Server. Dự án áp dụng kiến trúc Areas để phân chia các module quản trị, nhân viên và khách hàng một cách khoa học.'
        : 'A Pharmacy Management Web Application built with ASP.NET MVC, Entity Framework, and SQL Server. The system utilizes Areas architecture for modular organization of Admin, Staff, and Customer functionalities.',
      image: imgPharma1,
      gallery: [imgPharma1, imgPharma2, imgPharma3, imgPharma4, imgPharma5],
      technologies: ['ASP.NET MVC', 'Entity Framework', 'SQL Server', 'C#', 'Areas Architecture', 'Bootstrap'],
      github: 'https://github.com/ntainguyenit/PharmaSphere',
      category: 'Website',
      keyFeatures: isVietnamese 
        ? ['Quản lý kho dược phẩm và hạn sử dụng.', 'Hệ thống bán hàng và in hóa đơn.', 'Phân quyền người dùng theo Areas.', 'Báo cáo doanh thu chi tiết.', 'Cơ chế Bảo mật & Băm mật khẩu (Hashing). Tích hợp thư viện BCrypt.Net-Next, một tiêu chuẩn bảo mật mạnh mẽ để băm mật khẩu trước khi lưu vào SQL Server.']
        : ['Pharmaceutical inventory & expiry management.', 'Point of Sale (POS) & billing system.', 'Role-based access control via Areas.', 'Detailed revenue reporting.', 'Security & Password Hashing. Integrated BCrypt.Net-Next, a strong security standard to hash passwords before saving to SQL Server.']
    },
    {
      id: 105,
      title: 'Tech Resources',
      description: isVietnamese ? 'Bộ sưu tập tuyển chọn các tài nguyên kỹ thuật số chuyên nghiệp dành cho nhà phát triển.' : 'A curated collection of professional digital resources for developers.',
      image: imgTechResources1,
      gallery: [imgTechResources1],
      technologies: ['TypeScript', 'React'],
      github: 'https://github.com/ntainguyenit/Tech-Resources',
      demo: 'https://techresrc.netlify.app/',
      category: 'Website',
      keyFeatures: isVietnamese ? ['Phân loại tài nguyên được chọn lọc.', 'Tìm kiếm và lọc động.', 'Giao diện người dùng hiện đại và phản hồi nhanh.'] : ['Curated resources categorization.', 'Dynamic search and filtering.', 'Responsive and modern user interface.'],
    },
    {
      id: 101,
      title: isVietnamese ? 'Quản lý Đào tạo' : 'Training Management',
      description: isVietnamese 
        ? 'Hệ thống Quản lý Sinh viên được xây dựng dựa trên kiến trúc MVC sử dụng PHP thuần.'
        : 'Student Management System built on MVC architecture using native PHP.',
      image: imgTrain1, 
      gallery: [imgTrain1, imgTrain2, imgTrain3],
      technologies: ['PHP', 'MySQL'], 
      github: 'https://github.com/ntainguyenit/Training-Management',
      category: 'Website',
      keyFeatures: isVietnamese 
        ? [
            'Quản lý hồ sơ sinh viên và giảng viên tập trung.',
            'Hệ thống đăng ký khóa học trực tuyến thời gian thực.',
            'Tự động tính toán điểm trung bình và xếp loại học lực.',
            'Xuất báo cáo thống kê dưới dạng biểu đồ trực quan.'
          ]
        : [
            'Centralized student and faculty profile management.',
            'Real-time online course registration system.',
            'Automated GPA calculation and academic grading.',
            'Visual chart-based statistical report export.'
          ],
      challenges: isVietnamese
        ? [
            {
              problem: 'Xử lý xung đột dữ liệu khi nhiều sinh viên cùng đăng ký một khóa học có giới hạn.',
              solution: 'Áp dụng cơ chế Database Transaction và Row-level Locking để đảm bảo tính nhất quán của dữ liệu.'
            },
            {
              problem: 'Hiệu suất truy vấn chậm khi danh sách sinh viên lên đến hàng chục nghìn bản ghi.',
              solution: 'Tối ưu hóa Index cho các bảng chính và sử dụng kỹ thuật Eager Loading để giảm số lượng truy vấn SQL.'
            }
          ]
        : [
            {
              problem: 'Handling data conflicts when multiple students register for a limited course simultaneously.',
              solution: 'Implemented Database Transactions and Row-level Locking to ensure data consistency.'
            },
            {
              problem: 'Slow query performance with student lists reaching tens of thousands of records.',
              solution: 'Optimized Indexes for main tables and used Eager Loading to reduce the number of SQL queries.'
            }
          ],
    },
    {
      id: 1,
      title: 'Infinite Voxel Runner',
      description: '3D endless runner game built with Unity',
      image: imgInfiniteVoxel,
      technologies: ['C#', 'Unity'],
      platforms: ['Windows', 'WebGL'],
      github: 'https://github.com/ntainguyenit/Infinite-Voxel-Runner',
      category: 'Game',
      keyFeatures: isVietnamese ? ['Hệ thống tạo màn chơi vô tận.', 'Cơ chế vật lý mượt mà.', 'Tối ưu hóa hiệu năng mobile.'] : ['Infinite level generation system.', 'Smooth physics mechanics.', 'Mobile performance optimization.'],
    },
    {
      id: 103,
      title: isVietnamese ? 'Ứng dụng Desktop' : 'Desktop App',
      description: isVietnamese ? 'Phát triển Ứng dụng Desktop sử dụng C#, .NET.' : 'Exercises and assignments for the Desktop Application Development course.',
      image: imgDesktop1,
      gallery: [imgDesktop1, imgDesktop2],
      technologies: ['C#', 'SQL Server'],
      github: 'https://github.com/ntainguyenit/CSharp-DesktopApp-Development',
      category: isVietnamese ? 'Desktop' : 'Desktop',
      keyFeatures: isVietnamese ? ['Quản lý dữ liệu người dùng.', 'Hệ thống báo cáo tùy chỉnh.', 'Giao diện WinForms hiện đại.'] : ['User data management.', 'Custom reporting system.', 'Modern WinForms UI.'],
    },
  ];

  const sideProjects: Project[] = [
        {
      id: 104,
      title: isVietnamese ? 'Tin nhắn Socket' : 'Message Socket',
      description: isVietnamese ? 'Dự án lập trình phân tán Java sử dụng kỹ thuật Socket trong môi trường NetBeans.' : 'A Java distributed programming project using Socket in NetBeans.',
      image: imgAutoMsg1,
      gallery: [imgAutoMsg1, imgAutoMsg2],
      technologies: ['Java', 'Socket', 'NetBeans'],
      github: 'https://github.com/ntainguyenit/AutoMessageSystem-Socket',
      category: isVietnamese ? 'Hệ thống' : 'System',
      keyFeatures: isVietnamese ? ['Truyền tin nhắn thời gian thực.', 'Hỗ trợ đa kết nối client-server.', 'Mã hóa dữ liệu cơ bản.'] : ['Real-time message transmission.', 'Multi-client-server connection support.', 'Basic data encryption.'],
    },
    {
      id: 3,
      title: 'Lucky Wheel Game',
      description: isVietnamese ? 'Một trò chơi cá cược tương tác hiện đại trên Android được xây dựng bằng Kotlin, Jetpack Compose và kiến trúc MVVM.' : 'A modern Android interactive betting game built with Kotlin, Jetpack Compose, and MVVM architecture.',
      image: imgLuckyWheel1,
      gallery: [imgLuckyWheel1, imgLuckyWheel2, imgLuckyWheel3, imgLuckyWheel4, imgLuckyWheel5],
      technologies: ['Kotlin', 'Android', 'Jetpack Compose', 'MVVM'],
      platforms: ['Android'],
      github: 'https://github.com/ntainguyenit/lucky-wheel-game-android',
      category: 'Game',
      keyFeatures: isVietnamese ? [
        'Custom Canvas Animations: Vòng quay và hiệu ứng giảm tốc mượt mà của kim chỉ được lập trình hoàn toàn bằng toán học thông qua animateFloatAsState và Coroutines của Compose, thể hiện hiệu năng render UI phức tạp.',
        'State-Driven UI: Toàn bộ state của app (Số dư, Kết quả quay, Cài đặt Admin) luân chuyển một chiều một cách chặt chẽ từ ViewModel xuống, đảm bảo không bao giờ xảy ra lỗi bất đồng bộ UI.',
        'Admin RTP Controller: Bảng điều khiển cấu hình động cho phép điều chỉnh Tỉ lệ Thắng (RTP - Return to Player) theo thời gian thực, minh chứng cho việc áp dụng data-binding chuyên sâu.'
      ] : [
        'Custom Canvas Animations: Smooth rotation and deceleration effects programmed using math, animateFloatAsState, and Coroutines in Compose, showcasing complex UI rendering performance.',
        'State-Driven UI: App states (Balance, Spin Result, Admin Settings) flow strictly downwards from the ViewModel, eliminating asynchronous UI bugs.',
        'Admin RTP Controller: Dynamic configuration dashboard allowing real-time Return to Player (RTP) adjustments, demonstrating deep data-binding implementation.'
      ],
    },
    {
      id: 2,
      title: 'Nexus Chat',
      description: isVietnamese ? 'Ứng dụng nhắn tin theo thời gian thực được xây dựng bằng .NET sử dụng Clean Architecture, SignalR, EF Core, Redis và xác thực JWT.' : 'Real-time chat application built with .NET using Clean Architecture, SignalR, EF Core, Redis, and JWT authentication.',
      image: 'https://images.unsplash.com/photo-1515378791036-0648a3ef77b2?q=80&w=1000&auto=format&fit=crop',
      technologies: ['C#', '.Net', 'SignalR'],
      platforms: ['Web'],
      github: 'https://github.com/ntainguyenit/NexusChat',
      category: 'Web',
      keyFeatures: isVietnamese ? ['Nhắn tin theo thời gian thực.', 'Xác thực bằng JWT.', 'Thiết kế theo Clean Architecture.'] : ['Real-time messaging.', 'JWT authentication.', 'Clean Architecture design.'],
    },
    {
      id: 106,
      title: 'Hotel Core',
      description: isVietnamese 
        ? 'Hệ thống Quản lý Khách sạn doanh nghiệp được xây dựng trên kiến trúc Clean Architecture, sử dụng ASP.NET Core MVC và Dapper.'
        : 'Enterprise Hotel Management System built on Clean Architecture, using ASP.NET Core MVC and Dapper.',
      image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?q=80&w=1000&auto=format&fit=crop',
      gallery: ['https://images.unsplash.com/photo-1566073771259-6a8506099945?q=80&w=1000&auto=format&fit=crop'],
      technologies: ['C#', 'ASP.NET Core MVC', 'Clean Architecture', 'Dapper', 'SQL Server'],
      github: 'https://github.com/ntainguyenit/HotelCore',
      category: 'Website',
      keyFeatures: isVietnamese ? ['Quản lý buồng phòng thông minh.', 'Hệ thống đặt phòng trực tuyến.', 'Báo cáo tài chính chi tiết.'] : ['Smart room management.', 'Online booking system.', 'Detailed financial reports.'],
    },
    {
      id: 102,
      title: isVietnamese ? 'Khám phá Sách' : 'Book Explorer',
      description: isVietnamese 
        ? 'Book Explorer là ứng dụng Flutter cho phép duyệt sách, xem chi tiết và lưu danh sách yêu thích.'
        : 'Book Explorer is a Flutter app for browsing books, viewing details, and saving favorites.',
      image: 'https://images.unsplash.com/photo-1495446815901-a7297e633e8d?q=80&w=1000&auto=format&fit=crop',
      technologies: ['Flutter', 'Dart', 'API', 'Postman'],
      github: 'https://github.com/ntainguyenit/book-explorer',
      category: isVietnamese ? 'Mobile' : 'Mobile',
      keyFeatures: isVietnamese ? ['Tìm kiếm sách theo tiêu đề, tác giả.', 'Xem chi tiết thông tin sách.', 'Quản lý sách yêu thích offline.'] : ['Search books by title, author.', 'View detailed book information.', 'Offline favorites management.'],
    },
  ];



  return (
    <section id="projects" className="py-20 px-4 relative overflow-hidden">
      {/* Background Decor */}
      <div className="absolute top-1/4 -right-64 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-1/4 -left-64 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none"></div>

      <style>
        {`
          @keyframes scroll { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
          .animate-scroll { animation: scroll 20s linear infinite; }
          .animate-scroll:hover { animation-play-state: paused; }

          @keyframes flowLaser {
            0% { stroke-dashoffset: 1200; }
            100% { stroke-dashoffset: 0; }
          }
          @keyframes flowDottedReverse {
            0% { stroke-dashoffset: 0; }
            100% { stroke-dashoffset: -200; }
          }
          @keyframes pulseScale {
            0%, 100% { transform: scale(1); opacity: 0.8; }
            50% { transform: scale(1.15); opacity: 1; }
          }
          @keyframes blink {
            0% { opacity: 0; }
            20% { opacity: 1; }
            100% { opacity: 0; }
          }
          .dot-1 { animation: blink 1.5s infinite; animation-delay: 0s; }
          .dot-2 { animation: blink 1.5s infinite; animation-delay: 0.2s; }
          .dot-3 { animation: blink 1.5s infinite; animation-delay: 0.4s; }
        `}
      </style>

      <div className="max-w-7xl mx-auto relative z-10 md:pl-24 md:pr-24">
        
        {/* --- SECTION 1: HEADER & STATS --- */}
        <div className="text-center mb-16" data-aos="fade-up">
            <h2 className={`text-5xl md:text-6xl text-center mb-4 uppercase text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-gray-700 to-gray-900 dark:from-white dark:via-gray-200 dark:to-gray-400 py-4 font-black tracking-tighter leading-normal`}>
              {t.featuredTitle}
            </h2>
            <div data-aos="fade-up" data-aos-delay="100">
                <StatsSection />
            </div>
        </div>

        {/* --- SECTION 2: COMBINED PROJECTS --- */}
        <div id="all-projects" data-aos="fade-up" className="scroll-mt-24 relative">
          {/* Background Decor */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-20 dark:opacity-10 z-0">
             <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-blue-200 dark:text-blue-900/20 text-[20rem] font-black opacity-20 select-none">LAB</div>
          </div>

          {/* Merge projects logic */}
          {(() => {
            const allProjects = [...featuredProjects, ...sideProjects];
            return (
              <>
                <div key={currentPage} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 relative z-10">
                  {allProjects.slice((currentPage - 1) * projectsPerPage, currentPage * projectsPerPage).map((project, index) => (
                    <div 
                      key={project.id} 
                      onClick={() => setSelectedDocProject(project)}
                      className="group relative bg-gray-100/90 dark:bg-zinc-900/50 backdrop-blur-md border border-gray-200 dark:border-zinc-800 rounded-2xl overflow-hidden hover:border-cyan-500/50 transition-all duration-500 bounce-pop-effect hover:shadow-2xl hover:shadow-cyan-500/10 flex flex-col cursor-pointer active:scale-95" 
                      data-aos="fade-up" 
                      data-aos-delay={index * 50}
                    >
                      {/* Image Overlay with View Detail */}
                      <div className="relative aspect-video overflow-hidden">
                        {project.video ? (
                          <>
                            <video
                              src={project.video}
                              autoPlay
                              loop
                              muted
                              playsInline
                              className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 select-none ${project.videoMobile ? 'hidden md:block' : ''}`}
                            />
                            {project.videoMobile && (
                              <video
                                src={project.videoMobile}
                                autoPlay
                                loop
                                muted
                                playsInline
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 select-none md:hidden"
                              />
                            )}
                          </>
                        ) : (
                          <img 
                            src={project.image} 
                            alt={project.title} 
                            draggable="false"
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 select-none" 
                            loading="lazy"
                            decoding="async"
                          />
                        )}
                        {/* Updating Note */}
                        {project.image.includes('data:image/svg') && (
                          <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-transparent text-gray-800 text-[10px] font-bold flex items-center z-20">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500 mr-1.5 animate-pulse shadow-[0_0_8px_rgba(34,197,94,0.6)]"></span>
                            {isVietnamese ? 'Đang cập nhật' : 'Updating'}
                            <span className="flex w-3 text-left ml-0.5">
                              <span className="dot-1">.</span>
                              <span className="dot-2">.</span>
                              <span className="dot-3">.</span>
                            </span>
                          </div>
                        )}
                        {/* Category Tag */}
                        <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-black/50 backdrop-blur-sm text-white text-[10px] font-bold flex items-center z-20">

                          {project.category.toUpperCase()}
                        </div>
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100 backdrop-blur-[2px]">
                          <div className="px-5 py-2.5 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-white font-black text-sm tracking-[0.2em] shadow-2xl transform translate-y-4 group-hover:translate-y-0 transition-all duration-500">
                            VIEW DETAIL
                          </div>
                        </div>
                      </div>

                      <div className="p-5 flex flex-col flex-1">
                        <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors leading-tight">
                          {project.title}
                        </h4>
                        
                        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 leading-relaxed line-clamp-2">
                          {project.description}
                        </p>

                        <div className="flex flex-wrap gap-1.5 mb-6">
                          {project.technologies.map((tech, i) => (
                            <span key={i} className="px-2 py-0.5 text-[10px] font-medium text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-100 dark:border-blue-800/30">
                              {tech}
                            </span>
                          ))}
                        </div>

                        <div className="mt-auto flex gap-2" onClick={e => e.stopPropagation()}>
                          {project.github && (
                            <button 
                              onClick={() => window.open(project.github, '_blank')} 
                              className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 bg-gray-900 dark:bg-white text-white dark:text-black rounded-xl font-semibold text-xs hover:bg-gray-700 dark:hover:bg-gray-200 transition-all shadow-sm active:scale-90"
                            >
                              <Code2 size={14} />{t.viewCode}
                            </button>
                          )}
                          {project.demo && (
                            <button 
                              onClick={() => window.open(project.demo, '_blank')} 
                              className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-xl font-bold text-xs hover:bg-blue-100 dark:hover:bg-blue-800 transition-all border border-blue-200 dark:border-blue-700 shadow-sm active:scale-90"
                            >
                              <ExternalLink size={14} />{t.liveDemo}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Call To Action - Contact on last page */}
                  {currentPage === Math.ceil(allProjects.length / projectsPerPage) && (
                      <div className="flex items-center justify-center p-4 md:p-6 bg-gradient-to-r from-blue-50/50 via-white to-cyan-50/50 dark:from-zinc-900/50 dark:via-zinc-800 dark:to-zinc-900/50 rounded-3xl border-2 border-dashed border-blue-200 dark:border-zinc-700 shadow-sm transition-all animate-[fadeInUp_0.5s_ease-out] group hover:border-blue-400 dark:hover:border-blue-500" data-aos="fade-up">
                        <p className="text-gray-600 dark:text-gray-300 text-sm md:text-base leading-relaxed text-center">
                          {isVietnamese ? (
                            <>Rất vui vì các dự án được bạn quan tâm, nếu cần thêm thông tin xin vui lòng liên hệ <button onClick={() => setShowContactModal(true)} className="font-bold text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-all cursor-pointer group-hover:underline decoration-2 underline-offset-2">Tại đây</button></>
                          ) : (
                            <>Glad you are interested in these projects, if you need more information please contact <button onClick={() => setShowContactModal(true)} className="font-bold text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-all cursor-pointer group-hover:underline decoration-2 underline-offset-2">Here</button></>
                          )}
                        </p>
                      </div>
                    )}
                </div>

                {/* Pagination Controls */}
                {allProjects.length > projectsPerPage && (
                  <div className="mt-16 flex justify-center items-center gap-3" data-aos="fade-up">
                    <button 
                      onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
                      disabled={currentPage === 1}
                      className={`p-2 rounded-xl border transition-all ${currentPage === 1 ? 'opacity-30 cursor-not-allowed border-gray-200' : 'hover:bg-blue-600 hover:text-white border-blue-600 text-blue-600'}`}
                    >
                      <ChevronLeft size={20} />
                    </button>

                    <div className="flex gap-2">
                      {[...Array(Math.ceil(allProjects.length / projectsPerPage))].map((_, i) => (
                        <button
                          key={i}
                          onClick={() => handlePageChange(i + 1)}
                          className={`w-10 h-10 rounded-xl font-bold transition-all ${currentPage === i + 1 ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30' : 'bg-gray-100 dark:bg-zinc-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-zinc-700'}`}
                        >
                          {i + 1}
                        </button>
                      ))}
                    </div>

                    <button 
                      onClick={() => handlePageChange(Math.min(currentPage + 1, Math.ceil(allProjects.length / projectsPerPage)))}
                      disabled={currentPage === Math.ceil(allProjects.length / projectsPerPage)}
                      className={`p-2 rounded-xl border transition-all ${currentPage === Math.ceil(allProjects.length / projectsPerPage) ? 'opacity-30 cursor-not-allowed border-gray-200' : 'hover:bg-blue-600 hover:text-white border-blue-600 text-blue-600'}`}
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>
                )}
              </>
            );
          })()}
        </div>





        {selectedDocProject && (
            <DocModal 
                project={selectedDocProject} 
                projectNumber={[...featuredProjects, ...sideProjects].findIndex(p => p.id === selectedDocProject.id) + 1}
                onClose={() => setSelectedDocProject(null)} 
                language={language} 
                onNextProject={() => {
                    const allProjects = [...featuredProjects, ...sideProjects];
                    const currentIndex = allProjects.findIndex(p => p.id === selectedDocProject.id);
                    const nextIndex = (currentIndex + 1) % allProjects.length;
                    setSelectedDocProject(allProjects[nextIndex]);
                }}
                onPrevProject={() => {
                    const allProjects = [...featuredProjects, ...sideProjects];
                    const currentIndex = allProjects.findIndex(p => p.id === selectedDocProject.id);
                    const prevIndex = (currentIndex - 1 + allProjects.length) % allProjects.length;
                    setSelectedDocProject(allProjects[prevIndex]);
                }}
            />
        )}

        {showContactModal && (
          <Contact 
            language={language} 
            isModal={true} 
            onClose={() => setShowContactModal(false)} 
          />
        )}

      </div>
    </section>
  );
};

export default React.memo(Projects);